<template>
  <div>
    <modal
      v-model="showModal"
      size="lg"
      :before-close="close"
      :footer="false"
    >
      <template #title>
        <h3>
          <i
            class="fa fa-trophy m-r-5"
            aria-hidden="true"
          />
          {{ header }}
        </h3>
      </template>
      <p>
        {{ intro }}
      </p>
      <div class="panel panel-default bottom">
        <div class="panel-heading">
          <h3 class="panel-title">
            <span>{{ introHeader }}</span>
            <v-select
              class="env-chooser pull-right"
              label="title"
              :options="modes"
              :value="selectedEnvironment"
              :clearable="false"
              :searchable="false"
              @input="setEnvironment"
            >
              <template #option="option">
                <h5 
                  class="env-title"
                  v-text="option.title"
                />
                <div 
                  class="env-content"
                  v-text="option.content"
                />
              </template>
            </v-select>
          </h3>
        </div>
        <div class="panel-body">
          <div 
            role="tablist"
            class="panel-group"
          >
            <div
              v-for="option in options"
              :key="option.index"
            >
              <div
                class="panel panel-default"
                :class="{ 'panel-dynamic-inbound-calls': option.index > 0 }"
              >
                <a
                  role="button"
                  data-toggle="collapse"
                  aria-expanded="false"
                  class="accordion-toggle"
                  data-cy="rtb-documentation-option-tab"
                  @click="toggleAccordion(option.index)"
                >
                  <div
                    role="tab"
                    aria-selected="false"
                    class="panel-heading panel-heading-dynamic-inbound-calls"
                  >
                    <h4
                      class="panel-title"
                    >
                      <a>{{ option.title }}</a>
                      <i
                        class="pull-right glyphicon"
                        :class="{'glyphicon-chevron-down': showAccordion[option.index], 'glyphicon-chevron-right': !showAccordion[option.index]}"
                      />
                    </h4>
                  </div>
                </a>
                <collapse v-model="showAccordion[option.index]">
                  <div class="panel-body">
                    <p>
                      {{ option.content }}
                    </p>
                    <div
                      v-for="callout in option.callouts"
                      :key="callout"
                      class="bs-callout bs-callout-info"
                    >
                      <i
                        class="glyphicon glyphicon-info-sign"
                        aria-hidden="true"
                      />{{ callout }}
                    </div>

                    <div 
                      v-for="subOption in option.subOptions"
                      :key="subOption.title"
                    >
                      <h5
                        class="m-b-5"
                      >
                        <a v-if="subOption.title">{{ subOption.title }}</a>
                      </h5>
                      <div
                        v-if="subOption.content"
                        v-tooltip.right="textBoxToolTip"
                        v-clipboard="{ copyText: GetFormattedContent(subOption.content) }"
                        class="code-container clickable"
                        @click="handleCodeContainerClick"
                      >
                        <a
                          class="copy-code btn btn-function"
                          aria-hidden="true"
                        >
                          <i
                            class="typcn typcn-tabs-outline"
                          />
                        </a>

                        <pre
                          v-if="subOption.content"
                          class="p-t-0 p-b-0"
                          :class="{'text-success': codeCopied}"
                        >
                          <code v-html="'<br>' + subOption.content" />
                        </pre>
                      </div>
                    </div>
                  </div>
                </collapse>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-default"
          @click="close"
        >
          {{ closeButtonLabel }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import sharedMessages from '@/core/locales';
import messages from './locales';

export default {
  name: 'DocumentationModal',
  props: {
    exposeCallerId: {
      type: Boolean,
      default() {
        return false;
      }
    },
    campaignId: {
      type: String,
      default() {
        return '';
      }
    },
    affiliate: {
      type: Object,
      default() {
        return {};
      }
    },
    header: {
      type: String,
      default() {
        return this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_HEADER');
      }
    },
    introHeader: {
      type: String,
      default() {
        return this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_TITLE');
      }
    },
    modesIntro: {
      type: String,
      default() {
        return 'In the options below you will find a placeholder called "Mode", this placeholder must be replaced by one of the following modes:';
      }
    },
    modes: {
      type: Array,
      default() {
        return [
          { title: 'Production', content: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_ENV_PROD') },
          { title: 'Test', content: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_ENV_TEST') },
          { title: 'Sandbox', content: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_ENV_SAND') },
        ];
      }
    },
    intro: {
      type: String,
      default() {
        return this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_INTRO');
      }
    },
    rtbId: {
      type: String,
      default() {
        return 'RTB-ID';
      }
    },
    closeButtonLabel: {
      type: String,
      default() {
        return this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_CLOSE_BUTTON');
      }
    },
    textBoxToolTip: {
      type: String,
      default() {
        return 'Click To Copy';
      }
    },
    onCloseModal: {
      type: Function,
      default() {
        return;
      }
    },
  },
  data() {
    return {
      selectedEnvironment: this.modes[0],
      showModal: true,
      showAccordion: [true, false, false],
      codeCopied: false,
      urls: { json: '', xml: ''}
    };
  },
  async created() {
    if (this.affiliate.id) {
      this.urls = await this.getRtbUrls({ campaignId: this.campaignId, affiliateId: this.affiliate.id });
    } else {
      this.urls = await this.getRtbUrls({ campaignId: this.campaignId });
    }
  },
  computed: {
    ...mapGetters('dynamicInboundCalls', [
      'getTags',
      'callerIdRequired',
      'journayaTcpa'
    ]),
    options() {
      return [
        {
          index: 0,
          title: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION1_TITLE'),
          content: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION1_CONTENT'),
          callouts: [
            this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION1_CALLOUT'),
            this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION1_SIPOK_CALLOUT')
          ],
          subOptions: [ 
            { title: 'URL - For JSON responses', content: this.urlJson + this._getQueryParamsStartIfNeeded(this.urlJson) + this._getTagsForGetRequest() },
            { title: 'URL - For XML responses', content: this.urlXml + this._getQueryParamsStartIfNeeded(this.urlXml) + this._getTagsForGetRequest() }
          ],
        },
        {
          index: 1,
          title: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION2_TITLE'),
          content: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION2_CONTENT'),
          callouts: [
            this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION2_CALLOUT'),
            this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION2_SIPOK_CALLOUT')
          ],
          subOptions: [
            { title: 'URL', content: this.urlJson},
            { title: 'HEADER', content: 'Content-Type: application/json' },
            { title: 'BODY', content: this._getTagsForJsonPostRequest() }
          ] 
        },
        {
          index: 2,
          title: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION3_TITLE'),
          content: this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION3_CONTENT'),
          callouts: [
            this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION3_CALLOUT'),
            this.$t('DYNAMIC_INBOUND_CALLS_DOCUMENTATION_MODAL_OPTION3_SIPOK_CALLOUT')
          ],
          subOptions: [
            { title: 'URL', content: this.urlXml},
            { title: 'HEADER', content: 'Content-Type: application/xml' },
            { title: 'BODY', content: this._getTagsForXmlPostRequest() }
          ],
        },
      ];
    },
    urlJson() {
      return this.urls?.json?.replace('/production/', `/${this.selectedEnvironment.title.toLowerCase()}/`);
    },
    urlXml() {
      return this.urls?.xml?.replace('/production/', `/${this.selectedEnvironment.title.toLowerCase()}/`);
    }
  },
  methods: {
    ...mapActions('dynamicInboundCalls', [
      'getRtbUrls'
    ]),
    close() {
      this.onCloseModal(this.affiliate);
      this.showModal = false;
      this.$emit('close');
    },
    toggleAccordion (index) {
      if (this.showAccordion[index]) {
        this.$set(this.showAccordion, index, false);
      } else {
        this.showAccordion = this.showAccordion.map((v, i) => i === index);
      }
    },
    handleCodeContainerClick() {
      this.codeCopied = !this.codeCopied;
      setTimeout((scope) => {
        scope.codeCopied = !scope.codeCopied;
      }, 300, this);
      //todo toaster notification
    },
    GetFormattedContent(content) {
      return content.replace(/&amp;/g, '&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/<rtbrequiredtag>(.+?)<\/rtbrequiredtag>/g,'$1');
    },
    setEnvironment(env) {
      this.selectedEnvironment = env;
    },
    _getTagsForGetRequest() {
      const requiredTagNames = this._getRequiredTagNames();
      const requiredTagsAndValues = '&<rtbrequiredtag>' + requiredTagNames.join('</rtbrequiredtag>=value&amp;<rtbrequiredtag>') + '</rtbrequiredtag>=value';
      
      const notRequiredTagNames = this._getNotRequiredTagNames();
      const notRequiredTagsAndValues = '&amp;' + notRequiredTagNames.join('=value&amp;') + '=value';

      const cidTagName = this._getCidTagName();
      const cidTag = cidTagName + '=14061571951';

      const exposeCallerIdTag = this.exposeCallerId ? '&exposeCallerId=yes' : '';

      if (requiredTagNames?.length > 0 || notRequiredTagNames?.length > 0) {
        return cidTag + exposeCallerIdTag +
        (requiredTagNames?.length > 0 ? requiredTagsAndValues : '') + 
        (notRequiredTagNames?.length > 0 ? notRequiredTagsAndValues : '');
      }

      const randomTagExamples = '&Tag1=TagValue1&Tag2=TagValue2';

      return cidTag + exposeCallerIdTag + randomTagExamples;
    },
    _getQueryParamsStartIfNeeded(url){
      return ( url != null && url.includes('?') ) ? '&amp;' : '?';
    },
    _getTagsForJsonPostRequest() {
      const requiredTagNames = this._getRequiredTagNames();
      const requiredTagsAndValues = '\n\t"<rtbrequiredtag>' + requiredTagNames.join('</rtbrequiredtag>": "value",\n\t"<rtbrequiredtag>') + '</rtbrequiredtag>": "value"';

      const notRequiredTagNames = this._getNotRequiredTagNames();
      const notRequiredTagsAndValues = '\n\t"' + notRequiredTagNames.join('": "value",\n\t"') + '": "value"';

      const cidTagName = this._getCidTagName();
      const cidTag = '{\n\t"' + cidTagName + '": "14061571951",';
      
      const exposeCallerIdTag = this.exposeCallerId ? '\n\t"ExposeCallerId": "yes",' : '';

      if (requiredTagNames?.length > 0 || notRequiredTagNames?.length > 0) {
        return cidTag + exposeCallerIdTag +
        (requiredTagNames?.length > 0 ? requiredTagsAndValues : '') + 
        (notRequiredTagNames?.length > 0 ? notRequiredTagsAndValues : '') +
        '\n}';
      }

      const randomTagExamples = '\n\t"Tag1": "TagValue1",\n\t"Tag2": "TagValue2"\n}';

      return cidTag + exposeCallerIdTag + randomTagExamples;
    },
    _getTagsForXmlPostRequest() {
      const requiredTagNames = this._getRequiredTagNames();
      const requiredTagsAndValues = requiredTagNames?.reduce((currentString, currentTag) => {
        return currentString + '\n\t<rtbrequiredtag>&lt;' + currentTag + '&gt;</rtbrequiredtag>value<rtbrequiredtag>&lt;/' + currentTag + '&gt;</rtbrequiredtag>';
      } , '');

      const notRequiredTagNames = this._getNotRequiredTagNames();
      const notRequiredTagsAndValues = notRequiredTagNames?.reduce((currentString, currentTag) => {
        return currentString + '\n\t&lt;' + currentTag + '&gt;value&lt;/' + currentTag + '&gt;';
      } , '');

      const cidTagName = this._getCidTagName();
      const cidTag = '&lt;bid_request&gt;\n\t&lt;' + cidTagName + '&gt;14061571951&lt;/' + cidTagName + '&gt;';

      const exposeCallerIdTag = this.exposeCallerId ? '\n\t&lt;ExposeCallerId&gt;yes&lt;/ExposeCallerId&gt;' : '';

      if (requiredTagNames?.length > 0 || notRequiredTagNames?.length > 0) {
        return cidTag + exposeCallerIdTag +
        (requiredTagNames?.length > 0 ? requiredTagsAndValues : '') + 
        (notRequiredTagNames?.length > 0 ? notRequiredTagsAndValues : '') + 
        '\n&lt;/bid_request&gt;';
      }
      
      const randomTagExamples = '\n\t&lt;Tag1&gt;TagValue1&lt;/Tag1&gt;\n\t&lt;Tag2&gt;TagValue2&lt;/Tag2&gt;\n&lt;/bid_request&gt;';

      return cidTag + exposeCallerIdTag + randomTagExamples;
    },
    _getRequiredTagNames() {
      const tags = this.getTags(this.campaignId, this.affiliate.id);
      const requiredTags = tags?.filter(t => t.required).map(rt => rt.tag);

      if (this.journayaTcpa(this.campaignId, this.affiliate.id) === 'Required') {
        requiredTags.push('EntityCode', 'JleadId');
      }

      return [...new Set(requiredTags)];
    },
    _getNotRequiredTagNames() {
      const tags = this.getTags(this.campaignId, this.affiliate.id);
      const notRequiredTags = tags?.filter(t => !t.required).map(rt => rt.tag);

      if (this.journayaTcpa(this.campaignId, this.affiliate.id) === 'On') {
        notRequiredTags.push('EntityCode', 'JleadId');
      }

      return [...new Set(notRequiredTags)];
    },
    _getCidTagName(){
      let cid = 'CID';

      if (this.callerIdRequired(this.campaignId, this.affiliate.id)) {
        cid = '<rtbrequiredtag>' + cid + '</rtbrequiredtag>';
      }

      return cid;
    }
  },
  i18n: {
    locale: 'en',
    sharedMessages,
    messages
  }
};
</script>

<style lang="scss">
  rtbrequiredtag {
    font-size: 125%;
    font-weight: bolder;
  }

  .env-chooser {
    margin-top: -4px;
    & .vs__selected{
      height: 28px;
    }
    & .vs__search::placeholder,
    & .vs__dropdown-toggle,
    & .vs__dropdown-menu {
      width: 435px;
      overflow-x: hidden;
    }
    .env-title {
      margin: 0;
      margin-top: 2px;
      text-transform: capitalize;
    }
    .env-content {
      color: var(--reset-body-font-color);
      font-size: smaller;
      word-wrap: normal;
      white-space: -webkit-pre-wrap; /* Newer versions of Chrome/Safari*/
      word-break: normal;
      white-space: normal;
      margin-bottom: 2px;
    }
  }
</style>